import { hoyLocal, fechaLocal, sumarDias } from '@/lib/fechas';
import { useState, useEffect, useCallback } from 'react';
import api, { formatoMXN } from '@/lib/api';
import { getWhatsAppLink } from '@/lib/whatsapp';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import FotosSubir from '@/componentes/FotosSubir';
import { ListOrdered, Plus, Clock, Droplets, CheckCircle2, Car, XCircle, CreditCard, MessageCircle, ArrowRight, ArrowLeft, Edit2, ChevronLeft, ChevronRight, FileDown, Send, X, UserCircle, Printer, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexto/AuthContexto';

const ESTADOS = {
  en_espera: { label: 'En Espera', color: 'bg-amber-500/20 text-amber-400 border-amber-800', icon: Clock },
  lavando: { label: 'Lavando', color: 'bg-blue-500/20 text-blue-400 border-blue-800', icon: Droplets },
  listo: { label: 'Listo', color: 'bg-green-500/20 text-green-400 border-green-800', icon: CheckCircle2 },
  entregado: { label: 'Entregado', color: 'bg-violet-500/20 text-violet-400 border-violet-800', icon: Car },
  cancelado: { label: 'Cancelado', color: 'bg-red-500/20 text-red-400 border-red-800', icon: XCircle },
};

const SIGUIENTE_ESTADO = { en_espera: 'lavando', lavando: 'listo', listo: 'entregado' };
const ESTADO_ANTERIOR = { lavando: 'en_espera', listo: 'lavando', entregado: 'listo' };

export default function CarwashTurnos() {
  const { featuresPlan } = useAuth();
  const tieneTicketTermico = featuresPlan === null || featuresPlan.includes('ticket_termico');
  const [turnos, setTurnos] = useState([]);
  // Tick cada 60s para refrescar el "hace X min" en las tarjetas sin recargar
  const [ahora, setAhora] = useState(() => new Date());
  useEffect(() => {
    const iv = setInterval(() => setAhora(new Date()), 60000);
    return () => clearInterval(iv);
  }, []);
  const [config, setConfig] = useState(null);
  const [servicios, setServicios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [pagoDialog, setPagoDialog] = useState(null);
  const [editDialog, setEditDialog] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [fechaFiltro, setFechaFiltro] = useState(hoyLocal());
  const [form, setForm] = useState({ placa: '', tipo_vehiculo: 'auto', color: '', cliente_nombre: '', cliente_telefono: '', servicios_ids: [], operario_id: '', notas: '', cargo_extra_descripcion: '', cargo_extra_monto: '', cargo_extra_comision: false });
  const [editForm, setEditForm] = useState({ placa: '', tipo_vehiculo: 'auto', color: '', cliente_nombre: '', cliente_telefono: '', servicios_ids: [], operario_id: '', notas: '', precio_final: '', descuento_monto: '', metodo_pago: 'efectivo', estado: '', fecha: '', pagado: false });
  const [operarios, setOperarios] = useState([]);
  const [productosMostrador, setProductosMostrador] = useState([]);
  const [turnoActivo, setTurnoActivo] = useState(null);
  const [agregandoProd, setAgregandoProd] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [metodoPago, setMetodoPago] = useState('efectivo');
  const [descuentoInfo, setDescuentoInfo] = useState(null);
  const [aplicarDescuento, setAplicarDescuento] = useState(false);
  const [descuentoManual, setDescuentoManual] = useState('');
  // Cargo extra flexible en el cobro (descripcion + monto + si genera comision)
  const [cargoExtraDesc, setCargoExtraDesc] = useState('');
  const [cargoExtraMonto, setCargoExtraMonto] = useState('');
  const [cargoExtraComision, setCargoExtraComision] = useState(false);
  // Cliente search + vehicles
  const [clienteEncontrado, setClienteEncontrado] = useState(null);
  const [vehiculosCliente, setVehiculosCliente] = useState([]);
  const [vehiculoSeleccionado, setVehiculoSeleccionado] = useState('');
  const [agregarNuevoVehiculo, setAgregarNuevoVehiculo] = useState(false);
  const [buscandoCliente, setBuscandoCliente] = useState(false);

  const buscarClientePorTel = useCallback(async (tel) => {
    if (!tel || tel.length < 8) {
      setClienteEncontrado(null); setVehiculosCliente([]);
      setVehiculoSeleccionado(''); setAgregarNuevoVehiculo(false);
      return;
    }
    setBuscandoCliente(true);
    try {
      const res = await api.get(`/carwash/clientes/buscar?telefono=${encodeURIComponent(tel)}`);
      if (res.data.encontrado) {
        setClienteEncontrado(res.data);
        setVehiculosCliente(res.data.vehiculos || []);
        const principal = (res.data.vehiculos || []).find(v => v.es_principal) || (res.data.vehiculos || [])[0];
        if (principal) {
          setVehiculoSeleccionado(principal.id);
          setForm(f => ({
            ...f,
            placa: principal.placa,
            tipo_vehiculo: principal.tipo_vehiculo || 'auto',
            color: principal.color || '',
            cliente_nombre: res.data.cliente.nombre || '',
          }));
        } else {
          setAgregarNuevoVehiculo(true);
          setForm(f => ({ ...f, cliente_nombre: res.data.cliente.nombre || '' }));
        }
      } else {
        setClienteEncontrado(null); setVehiculosCliente([]);
        setVehiculoSeleccionado(''); setAgregarNuevoVehiculo(false);
      }
    } catch { /* silent */ }
    finally { setBuscandoCliente(false); }
  }, []);

  const seleccionarVehiculo = (vehId) => {
    setVehiculoSeleccionado(vehId);
    setAgregarNuevoVehiculo(false);
    const v = vehiculosCliente.find(x => x.id === vehId);
    if (v) setForm(f => ({ ...f, placa: v.placa, tipo_vehiculo: v.tipo_vehiculo || 'auto', color: v.color || '' }));
  };

  const activarNuevoVehiculo = () => {
    setVehiculoSeleccionado('');
    setAgregarNuevoVehiculo(true);
    setForm(f => ({ ...f, placa: '', tipo_vehiculo: 'auto', color: '' }));
  };

  const resetForm = () => {
    setForm({ placa: '', tipo_vehiculo: 'auto', color: '', cliente_nombre: '', cliente_telefono: '', servicios_ids: [], operario_id: '', notas: '', cargo_extra_descripcion: '', cargo_extra_monto: '', cargo_extra_comision: false });
    setClienteEncontrado(null); setVehiculosCliente([]);
    setVehiculoSeleccionado(''); setAgregarNuevoVehiculo(false);
  };

  const cargar = useCallback(async () => {
    try {
      let url = `/carwash/turnos?fecha=${fechaFiltro}`;
      if (filtroEstado) url += `&estado=${filtroEstado}`;
      const [tRes, sRes] = await Promise.all([
        api.get(url),
        api.get('/carwash/servicios')
      ]);
      setTurnos(tRes.data);
      setServicios(sRes.data.filter(s => s.activo !== false));
    } catch { /* silent */ }
    finally { setCargando(false); }
  }, [filtroEstado, fechaFiltro]);

  useEffect(() => { cargar(); const i = setInterval(cargar, 15000); return () => clearInterval(i); }, [cargar]);
  useEffect(() => { api.get('/configuracion').then(r => setConfig(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    // Solo operarios activos que pueden ser asignados
    api.get('/carwash/staff').then(r => {
      setOperarios((r.data || []).filter(s =>
        s.activo !== false && ['operario', 'lavador'].includes((s.puesto || '').toLowerCase())
      ));
    }).catch(() => {});
  }, []);

  const crearTurno = async () => {
    if (!form.placa) { toast.error('La placa es obligatoria'); return; }
    if (!form.servicios_ids || form.servicios_ids.length === 0) { toast.error('Selecciona al menos un servicio'); return; }
    // ── Validacion anti-duplicados ──
    // Mismo cliente + MISMO vehiculo (placa) activo hoy → bloquear.
    // Mismo cliente + DIFERENTE vehiculo (placa distinta) → permitido.
    const placaNueva = form.placa.trim().toUpperCase();
    const duplicado = turnos.find(t =>
      (t.placa || '').trim().toUpperCase() === placaNueva &&
      !['entregado', 'cancelado'].includes(t.estado)
    );
    if (duplicado) {
      toast.error(`Ya existe un turno activo con la placa ${placaNueva} (${duplicado.numero_turno}). Si es otro vehículo del mismo cliente, usa una placa diferente.`);
      return;
    }
    setGuardando(true);
    try {
      const { servicios_ids, ...rest } = form;
      await api.post('/carwash/turnos', {
        ...rest,
        placa: placaNueva,
        servicios: servicios_ids.map(id => ({ id })),
      });
      toast.success('Turno creado');
      setDialog(false);
      resetForm();
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al crear turno'); }
    finally { setGuardando(false); }
  };

  const cambiarEstado = async (turnoId, nuevoEstado) => {
    try {
      await api.patch(`/carwash/turnos/${turnoId}/estado`, { estado: nuevoEstado });
      toast.success(`Estado: ${ESTADOS[nuevoEstado]?.label}`);
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const pagarTurno = async (turnoId) => {
    const porcentaje = aplicarDescuento ? (parseFloat(descuentoManual) || descuentoInfo?.descuento_porcentaje || 0) : 0;
    const payload = { metodo_pago: metodoPago, descuento_porcentaje: porcentaje };
    // Cargo extra flexible (opcional) — se envía siempre para permitir corregir o
    // limpiar un cargo agregado previamente en Crear/Editar.
    const cargo = Math.max(0, parseFloat(cargoExtraMonto) || 0);
    payload.cargo_extra_monto = cargo;
    payload.cargo_extra_descripcion = cargo > 0 ? cargoExtraDesc.trim() : '';
    payload.cargo_extra_comision = cargo > 0 ? cargoExtraComision : false;
    // Reasignar operario si cambió durante el cobro
    if (turnoActivo && (turnoActivo.operario_id || '') !== (pagoDialog?.operario_id || '')) {
      payload.operario_id = turnoActivo.operario_id || '';
    }
    try {
      await api.patch(`/carwash/turnos/${turnoId}/pagar`, payload);
      toast.success('Pago registrado');
      setPagoDialog(null);
      setTurnoActivo(null);
      setDescuentoInfo(null);
      setAplicarDescuento(false);
      setDescuentoManual('');
      setCargoExtraDesc('');
      setCargoExtraMonto('');
      setCargoExtraComision(false);
      cargar();
    } catch { toast.error('Error al registrar pago'); }
  };

  const cargarProductosMostrador = useCallback(async () => {
    try {
      const r = await api.get('/carwash/productos/mostrador');
      setProductosMostrador(r.data || []);
    } catch { /* silent */ }
  }, []);

  const abrirPago = async (turno) => {
    setPagoDialog(turno);
    setTurnoActivo(turno);
    setMetodoPago('efectivo');
    setAplicarDescuento(false);
    setDescuentoManual('');
    setDescuentoInfo(null);
    setCargoExtraDesc(turno.cargo_extra_descripcion || '');
    setCargoExtraMonto(turno.cargo_extra_monto ? String(turno.cargo_extra_monto) : '');
    setCargoExtraComision(!!turno.cargo_extra_comision);
    cargarProductosMostrador();
    try {
      const params = new URLSearchParams();
      if (turno.placa) params.set('placa', turno.placa);
      if (turno.cliente_telefono) params.set('telefono', turno.cliente_telefono);
      const res = await api.get(`/carwash/cliente-frecuente?${params.toString()}`);
      setDescuentoInfo(res.data);
      // Auto-aplicar premio de lealtad si toca
      if (res.data.es_premio) {
        setAplicarDescuento(true);
        const pct = res.data.lealtad_tipo === 'gratis' ? 100 : res.data.lealtad_descuento_pct;
        setDescuentoManual(String(pct));
      } else if (res.data.es_frecuente) {
        setAplicarDescuento(true);
        setDescuentoManual(String(res.data.descuento_porcentaje));
      }
    } catch { /* silent */ }
  };

  const agregarProductoAlTurno = async (producto) => {
    if (!turnoActivo || producto.stock <= 0) return;
    setAgregandoProd(producto.id);
    try {
      const r = await api.post(`/carwash/turnos/${turnoActivo.id}/productos`, {
        producto_id: producto.id, cantidad: 1,
      });
      setTurnoActivo(r.data);
      // Actualizar stock local, quitar si stock=0
      setProductosMostrador(prev => prev
        .map(p => p.id === producto.id ? { ...p, stock: p.stock - 1 } : p)
        .filter(p => p.stock > 0));
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al agregar'); }
    finally { setAgregandoProd(''); }
  };

  const quitarProductoDelTurno = async (linea_id) => {
    if (!turnoActivo) return;
    try {
      const r = await api.delete(`/carwash/turnos/${turnoActivo.id}/productos/${linea_id}`);
      setTurnoActivo(r.data);
      cargarProductosMostrador();
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al quitar'); }
  };

  const abrirEditar = (t) => {
    const ids = Array.isArray(t.servicios) && t.servicios.length > 0
      ? t.servicios.map(s => s.id)
      : (t.servicio_id ? [t.servicio_id] : []);
    setEditForm({
      placa: t.placa || '', tipo_vehiculo: t.tipo_vehiculo || 'auto', color: t.color || '',
      cliente_nombre: t.cliente_nombre || '', cliente_telefono: t.cliente_telefono || '',
      servicios_ids: ids, operario_id: t.operario_id || '', notas: t.notas || '',
      cargo_extra_descripcion: t.cargo_extra_descripcion || '',
      cargo_extra_monto: t.cargo_extra_monto ? String(t.cargo_extra_monto) : '',
      cargo_extra_comision: !!t.cargo_extra_comision,
      precio_final: t.precio_final != null ? String(t.precio_final) : (t.precio != null ? String(t.precio) : ''),
      descuento_monto: t.descuento_monto != null ? String(t.descuento_monto) : '',
      metodo_pago: t.metodo_pago || 'efectivo',
      estado: t.estado || 'recibido',
      fecha: t.fecha || hoyLocal(),
      pagado: !!t.pagado,
    });
    setEditDialog(t);
  };

  const guardarEdicion = async () => {
    if (!editForm.placa) { toast.error('La placa es obligatoria'); return; }
    setGuardando(true);
    try {
      const { servicios_ids, precio_final, descuento_monto, ...rest } = editForm;
      const payload = {
        ...rest,
        servicios: (servicios_ids || []).map(id => ({ id })),
      };
      if (precio_final !== '' && precio_final != null) payload.precio_final = parseFloat(precio_final);
      if (descuento_monto !== '' && descuento_monto != null) payload.descuento_monto = parseFloat(descuento_monto);
      await api.put(`/carwash/turnos/${editDialog.id}`, payload);
      toast.success('Turno actualizado');
      setEditDialog(null);
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al actualizar'); }
    finally { setGuardando(false); }
  };

  const enviarWhatsApp = (turno) => {
    if (!turno.cliente_telefono) { toast.error('Sin teléfono registrado'); return; }
    const tel = turno.cliente_telefono.replace(/\D/g, '');
    const telFmt = tel.length === 10 ? '52' + tel : tel;
    const estadoLabel = ESTADOS[turno.estado]?.label || turno.estado;
    const msg = `Hola ${turno.cliente_nombre || ''}! Tu vehículo (${turno.placa}) en servicio ${turno.servicio_nombre} está: *${estadoLabel}*. ${turno.estado === 'listo' ? 'Ya puedes pasar a recogerlo!' : ''}`;
    const a = document.createElement('a');
    a.href = getWhatsAppLink(telFmt, msg);
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
  };

  const cambiarFecha = (dir) => {
    setFechaFiltro(sumarDias(fechaFiltro, dir));
  };

  const diasSemana = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'];
  const fechaObj = new Date(fechaFiltro + 'T12:00:00');
  const esHoy = fechaFiltro === hoyLocal();

  // Filtro de busqueda: placa / tipo_vehiculo / cliente_nombre / numero_turno
  const busqLower = busqueda.trim().toLowerCase();
  const coincide = (t) => {
    if (!busqLower) return true;
    return (
      (t.placa || '').toLowerCase().includes(busqLower) ||
      (t.tipo_vehiculo || '').toLowerCase().includes(busqLower) ||
      (t.cliente_nombre || '').toLowerCase().includes(busqLower) ||
      (t.numero_turno || '').toLowerCase().includes(busqLower)
    );
  };
  const activos = turnos.filter(t => !['entregado', 'cancelado'].includes(t.estado) && coincide(t));
  const historial = turnos.filter(t => ['entregado', 'cancelado'].includes(t.estado) && coincide(t));

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="carwash-turnos-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="font-barlow text-3xl font-bold uppercase tracking-tight text-slate-50 flex items-center gap-3">
          <ListOrdered className="w-8 h-8 text-cyan-400" /> Turnos
        </h1>
        <div className="flex gap-2">
          <Select value={filtroEstado} onValueChange={setFiltroEstado}>
            <SelectTrigger className="w-[160px] bg-slate-900 border-slate-700 text-slate-200" data-testid="filtro-estado-select">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800">
              <SelectItem value="todos" className="text-slate-300">Todos</SelectItem>
              {Object.entries(ESTADOS).map(([k, v]) => <SelectItem key={k} value={k} className="text-slate-300">{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => setDialog(true)} className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold rounded-sm" data-testid="nuevo-turno-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuevo Turno
          </Button>
        </div>
      </div>

      {/* Date navigator */}
      <div className="flex items-center justify-between bg-slate-900/40 border border-slate-800 rounded-sm px-4 py-2">
        <button onClick={() => cambiarFecha(-1)} className="text-slate-400 hover:text-slate-200 p-1" data-testid="fecha-prev-btn">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="text-center">
          <p className="text-sm font-bold text-slate-100">
            {diasSemana[fechaObj.getDay()]} {fechaObj.getDate()} de {fechaObj.toLocaleDateString('es-MX', { month: 'long' })}
            {esHoy && <span className="text-cyan-400 text-xs ml-2">(Hoy)</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!esHoy && (
            <button onClick={() => setFechaFiltro(hoyLocal())}
              className="text-[10px] text-cyan-500 hover:text-cyan-400 font-bold" data-testid="fecha-hoy-btn">HOY</button>
          )}
          <button onClick={() => cambiarFecha(1)} className="text-slate-400 hover:text-slate-200 p-1" data-testid="fecha-next-btn">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Buscador (placa / tipo vehiculo / cliente) */}
      <div className="relative">
        <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <Input
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          placeholder="Buscar por placa, tipo de vehículo o cliente..."
          className="bg-slate-900/40 border-slate-800 text-slate-200 pl-9 pr-10 h-10"
          data-testid="buscar-turno-input"
        />
        {busqueda && (
          <button
            onClick={() => setBusqueda('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200"
            data-testid="limpiar-busqueda-btn"
            aria-label="Limpiar busqueda">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Turnos activos */}
      {activos.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {activos.map(t => {
            const est = ESTADOS[t.estado] || ESTADOS.en_espera;
            const Icon = est.icon;
            const sig = SIGUIENTE_ESTADO[t.estado];
            const ant = ESTADO_ANTERIOR[t.estado];
            return (
              <Card key={t.id} className="bg-slate-900/40 border-slate-800" data-testid={`turno-card-${t.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <span className="text-lg font-bold text-slate-100 font-mono">{t.placa}</span>
                      <p className="text-xs text-slate-500">{t.numero_turno} | {t.tipo_vehiculo}{t.color ? ` | ${t.color}` : ''}</p>
                      {t.created_at && (() => {
                        try {
                          const d = new Date(t.created_at);
                          const hora = d.toLocaleTimeString('es-MX', {
                            hour: '2-digit', minute: '2-digit',
                            timeZone: 'America/Mexico_City'
                          });
                          const diffMs = ahora - d;
                          const diffMin = Math.max(0, Math.floor(diffMs / 60000));
                          const diffH = Math.floor(diffMin / 60);
                          const rem = diffMin % 60;
                          let rel;
                          if (diffMin < 1) rel = 'recien creado';
                          else if (diffMin < 60) rel = `hace ${diffMin} min`;
                          else if (diffH < 24) rel = rem > 0 ? `hace ${diffH}h ${rem}min` : `hace ${diffH}h`;
                          else rel = `hace ${Math.floor(diffH / 24)}d`;
                          return (
                            <p className="text-[10px] font-mono mt-0.5 text-slate-400"
                               data-testid={`turno-tiempo-${t.id}`}>
                              <Clock className="w-2.5 h-2.5 inline mr-1 -mt-0.5" />
                              {rel} · {hora}
                            </p>
                          );
                        } catch { return null; }
                      })()}
                    </div>
                    <div className="flex items-center gap-1">
                      <Badge className={est.color + ' text-xs'} data-testid={`turno-estado-${t.id}`}>
                        <Icon className="w-3 h-3 mr-1" />{est.label}
                      </Badge>
                      <Button variant="ghost" size="icon" onClick={() => abrirEditar(t)}
                        className="text-slate-500 hover:text-cyan-400 h-7 w-7" data-testid={`editar-turno-${t.id}`}>
                        <Edit2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="mb-3 space-y-1">
                    {Array.isArray(t.servicios) && t.servicios.length > 1 ? (
                      <div className="space-y-0.5">
                        {t.servicios.map((s, idx) => (
                          <div key={idx} className="flex items-center justify-between text-xs">
                            <span className="text-cyan-400 truncate">• {s.nombre}</span>
                            <span className="font-mono text-slate-500 shrink-0">{formatoMXN(s.precio)}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-cyan-400 font-medium">{t.servicio_nombre}</p>
                    )}
                    {t.descuento_porcentaje > 0 ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500 line-through">{formatoMXN(t.precio)}</span>
                        <Badge className="bg-amber-500/20 text-amber-400 border-amber-800 text-[10px]">-{t.descuento_porcentaje}%</Badge>
                        <span className="text-sm font-mono text-slate-200 font-bold">{formatoMXN(t.precio_final)}</span>
                      </div>
                    ) : (
                      <p className="text-sm font-mono text-slate-200 font-bold">{formatoMXN(t.precio)}</p>
                    )}
                    {t.cliente_nombre && <p className="text-xs text-slate-500">{t.cliente_nombre}{t.cliente_telefono ? ` | ${t.cliente_telefono}` : ''}</p>}
                    {/* Selector inline de operario — asignar/reasignar en 1 clic desde la tarjeta */}
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs shrink-0 ${t.operario_nombre ? 'text-emerald-400 font-medium' : 'text-amber-500/70 italic'}`}>
                        {t.operario_nombre ? 'Operario:' : 'Sin operario:'}
                      </span>
                      <select
                        value={t.operario_id || ''}
                        onChange={async (e) => {
                          const opId = e.target.value;
                          const op = operarios.find(o => o.id === opId);
                          try {
                            await api.put(`/carwash/turnos/${t.id}`, {
                              operario_id: opId,
                              operario_nombre: op?.nombre || '',
                            });
                            toast.success(opId ? `Asignado a ${op?.nombre || ''}` : 'Operario removido');
                            cargar();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Error al reasignar');
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs bg-slate-950 border border-slate-700 text-slate-200 rounded px-2 py-0.5 flex-1 min-w-0 truncate hover:border-cyan-500/50 cursor-pointer"
                        data-testid={`operario-inline-${t.id}`}
                        disabled={t.estado === 'cancelado'}>
                        <option value="">-- Sin asignar --</option>
                        {operarios.map(op => <option key={op.id} value={op.id}>{op.nombre}</option>)}
                      </select>
                    </div>
                    {t.notas && <p className="text-xs text-slate-600 italic">{t.notas}</p>}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-slate-800">
                    {/* Retroceder */}
                    {ant && (
                      <Button size="sm" variant="ghost" onClick={() => cambiarEstado(t.id, ant)}
                        className="text-slate-500 hover:text-amber-400 text-xs" data-testid={`retroceder-turno-${t.id}`}>
                        <ArrowLeft className="w-3 h-3 mr-1" /> {ESTADOS[ant]?.label}
                      </Button>
                    )}
                    {/* Avanzar */}
                    {sig && !(sig === 'entregado' && !t.pagado) && (
                      <Button size="sm" onClick={() => cambiarEstado(t.id, sig)}
                        className="bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 text-xs" data-testid={`avanzar-turno-${t.id}`}>
                        <ArrowRight className="w-3 h-3 mr-1" /> {ESTADOS[sig]?.label}
                      </Button>
                    )}
                    {sig === 'entregado' && !t.pagado && (
                      <span className="text-[10px] text-amber-400 text-center">Cobrar antes de entregar</span>
                    )}
                    {!t.pagado && t.estado !== 'cancelado' && (
                      <Button size="sm" variant="ghost" onClick={() => abrirPago(t)}
                        className="text-green-400 hover:text-green-300 text-xs" data-testid={`pagar-turno-${t.id}`}>
                        <CreditCard className="w-3 h-3 mr-1" /> Cobrar
                      </Button>
                    )}
                    {t.pagado && <Badge className="bg-green-500/20 text-green-400 border-green-800 text-[10px]">Pagado</Badge>}
                    {t.pagado && (
                      <Button size="sm" variant="ghost" onClick={() => {
                        const tk = localStorage.getItem('token') || '';
                        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/carwash/turnos/${t.id}/ticket-pdf?token=${tk}`, '_blank');
                      }}
                        className="text-amber-400 hover:text-amber-300 text-xs" data-testid={`ticket-pdf-${t.id}`}>
                        <FileDown className="w-3 h-3" />
                      </Button>
                    )}
                    {t.pagado && tieneTicketTermico && (
                      <Button size="sm" variant="ghost" onClick={() => {
                        window.open(`/carwash/turnos/${t.id}/ticket-termico`, '_blank');
                      }}
                        className="text-cyan-400 hover:text-cyan-300 text-xs" data-testid={`ticket-termico-${t.id}`}
                        title="Imprimir ticket térmico 80mm">
                        <Printer className="w-3 h-3" />
                      </Button>
                    )}
                    {t.pagado && t.cliente_telefono && (
                      <Button size="sm" variant="ghost" onClick={() => {
                        const msg = `Ticket de servicio:\n${config?.nombre_taller || 'Car Wash'}\nFolio: ${t.numero_turno}\nServicio: ${t.servicio_nombre}\nTotal: $${t.precio_final || t.precio}\nMetodo: ${t.metodo_pago}\n\nGracias por su preferencia!`;
                        const url = getWhatsAppLink(t.cliente_telefono, msg);
                        window.open(url, '_blank');
                      }}
                        className="text-emerald-400 hover:text-emerald-300 text-xs" data-testid={`ticket-whatsapp-${t.id}`}>
                        <Send className="w-3 h-3" />
                      </Button>
                    )}
                    {t.cliente_telefono && (
                      <Button size="sm" variant="ghost" onClick={() => enviarWhatsApp(t)}
                        className="text-emerald-400 hover:text-emerald-300 text-xs" data-testid={`whatsapp-turno-${t.id}`}>
                        <MessageCircle className="w-3 h-3" />
                      </Button>
                    )}
                    {t.estado !== 'cancelado' && (
                      <Button size="sm" variant="ghost" onClick={() => cambiarEstado(t.id, 'cancelado')}
                        className="text-red-400/60 hover:text-red-400 text-xs" data-testid={`cancelar-turno-${t.id}`}>
                        <XCircle className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {activos.length === 0 && !cargando && (
        <Card className="bg-slate-900/40 border-slate-800">
          <CardContent className="p-12 text-center">
            <Droplets className="w-12 h-12 text-slate-700 mx-auto mb-3" />
            <p className="text-slate-500">No hay turnos activos</p>
            <p className="text-xs text-slate-600 mt-1">Crea un nuevo turno para empezar</p>
          </CardContent>
        </Card>
      )}

      {/* Historial del dia */}
      {historial.length > 0 && (
        <Card className="bg-slate-900/40 border-slate-800">
          <CardHeader className="pb-3"><CardTitle className="font-barlow text-lg text-slate-200">Historial del Día ({historial.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {historial.map(t => {
                const est = ESTADOS[t.estado];
                const ant = ESTADO_ANTERIOR[t.estado];
                return (
                  <div key={t.id} className="flex items-center justify-between p-3 bg-slate-800/30 rounded-sm" data-testid={`historial-turno-${t.id}`}>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-mono text-slate-300 font-bold">{t.placa}</span>
                      <span className="text-xs text-slate-500">{t.servicio_nombre}</span>
                      <Badge className={est?.color + ' text-[10px]'}>{est?.label}</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      {ant && (
                        <Button size="sm" variant="ghost" onClick={() => cambiarEstado(t.id, ant)}
                          className="text-slate-500 hover:text-amber-400 text-xs h-7" data-testid={`retroceder-hist-${t.id}`}>
                          <ArrowLeft className="w-3 h-3 mr-1" /> Regresar
                        </Button>
                      )}
                      {t.descuento_porcentaje > 0 && <Badge className="bg-amber-500/20 text-amber-400 border-amber-800 text-[10px]">-{t.descuento_porcentaje}%</Badge>}
                      <span className="text-sm font-mono text-slate-400">{formatoMXN(t.precio_final ?? t.precio)}</span>
                      {t.pagado ? <Badge className="bg-green-500/20 text-green-400 border-green-800 text-[10px]">{t.metodo_pago}</Badge> : <Badge className="bg-red-500/20 text-red-400 border-red-800 text-[10px]">Sin cobrar</Badge>}
                      {t.pagado && (
                        <Button size="sm" variant="ghost" onClick={() => {
                          const tk = localStorage.getItem('token') || '';
                          window.open(`${process.env.REACT_APP_BACKEND_URL}/api/carwash/turnos/${t.id}/ticket-pdf?token=${tk}`, '_blank');
                        }}
                          className="text-amber-400 hover:text-amber-300 text-xs h-7" data-testid={`hist-ticket-pdf-${t.id}`} title="Descargar ticket">
                          <FileDown className="w-3 h-3" />
                        </Button>
                      )}
                      {t.pagado && t.cliente_telefono && (
                        <Button size="sm" variant="ghost" onClick={() => {
                          const msg = `Ticket de servicio:\n${config?.nombre_taller || 'Car Wash'}\nFolio: ${t.numero_turno}\nServicio: ${t.servicio_nombre}\nTotal: $${t.precio_final || t.precio}\nMetodo: ${t.metodo_pago}\n\nGracias por su preferencia!`;
                          const url = getWhatsAppLink(t.cliente_telefono, msg);
                          window.open(url, '_blank');
                        }}
                          className="text-emerald-400 hover:text-emerald-300 text-xs h-7" data-testid={`hist-ticket-whatsapp-${t.id}`} title="Enviar por WhatsApp">
                          <Send className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog: Nuevo Turno */}
      <Dialog open={dialog} onOpenChange={(o) => { setDialog(o); if (!o) resetForm(); }}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md max-h-[90vh] overflow-y-auto" data-testid="nuevo-turno-dialog">
          <DialogHeader><DialogTitle className="font-barlow text-xl text-slate-100">Nuevo Turno</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* 1. Telefono (siempre primero) */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Telefono del cliente</Label>
              <div className="relative">
                <Input value={form.cliente_telefono}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setForm(f => ({ ...f, cliente_telefono: v }));
                    if (v.length === 10) buscarClientePorTel(v);
                    else if (clienteEncontrado) { setClienteEncontrado(null); setVehiculosCliente([]); setVehiculoSeleccionado(''); setAgregarNuevoVehiculo(false); }
                  }}
                  placeholder="10 digitos"
                  className="bg-slate-950 border-slate-700 text-slate-200 font-mono"
                  data-testid="turno-telefono-input" />
                {buscandoCliente && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-cyan-500">Buscando...</span>}
              </div>
            </div>

            {/* 2. Cliente encontrado */}
            {clienteEncontrado && (
              <div className="bg-cyan-500/10 border border-cyan-800 rounded-sm p-3 space-y-3" data-testid="cliente-encontrado-card">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-cyan-400 font-bold">Cliente Registrado</p>
                  <p className="text-base font-bold text-slate-100">{clienteEncontrado.cliente.nombre || 'Sin nombre'}</p>
                  <p className="text-xs text-slate-400">
                    {clienteEncontrado.cliente.total_visitas || 0} visitas · {formatoMXN(clienteEncontrado.cliente.total_gastado || 0)}
                    {clienteEncontrado.lealtad?.activo && (
                      <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold ${clienteEncontrado.lealtad.es_premio ? 'bg-amber-500/20 text-amber-400' : 'bg-cyan-500/20 text-cyan-400'}`}>
                        {clienteEncontrado.lealtad.es_premio ? '🎁 PROXIMO PREMIO' : `Visita ${clienteEncontrado.lealtad.visita_actual}/${clienteEncontrado.lealtad.meta}`}
                      </span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Selecciona vehiculo</p>
                  <div className="space-y-1.5">
                    {vehiculosCliente.map(v => (
                      <button key={v.id} type="button" onClick={() => seleccionarVehiculo(v.id)}
                        className={`w-full text-left px-3 py-2 rounded-sm text-sm border transition ${vehiculoSeleccionado === v.id ? 'bg-cyan-500/20 border-cyan-600 text-slate-100' : 'bg-slate-800/40 border-slate-700 text-slate-300 hover:border-slate-600'}`}
                        data-testid={`veh-opt-${v.id}`}>
                        <span className="font-mono font-bold">{v.placa}</span>
                        <span className="text-slate-500 text-xs ml-2">· {v.tipo_vehiculo}{v.color ? ` · ${v.color}` : ''}</span>
                        {v.es_principal && <span className="text-amber-400 text-[10px] ml-2">★ Principal</span>}
                      </button>
                    ))}
                    <button type="button" onClick={activarNuevoVehiculo}
                      className={`w-full text-left px-3 py-2 rounded-sm text-sm border-2 border-dashed transition ${agregarNuevoVehiculo ? 'bg-emerald-500/20 border-emerald-600 text-emerald-300' : 'border-slate-700 text-slate-400 hover:border-slate-500'}`}
                      data-testid="agregar-vehiculo-btn">
                      + Agregar otro vehiculo
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* 3. Datos del cliente (si NO existe) */}
            {!clienteEncontrado && form.cliente_telefono.length === 10 && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Nombre del cliente</Label>
                <Input value={form.cliente_nombre}
                  onChange={e => setForm({ ...form, cliente_nombre: e.target.value })}
                  placeholder="Nombre completo"
                  className="bg-slate-950 border-slate-700 text-slate-200" />
                <p className="text-[10px] text-slate-500 mt-1">Cliente nuevo - se registrara automaticamente</p>
              </div>
            )}

            {/* 4. Datos del vehiculo (siempre que haya placa que editar) */}
            {(!clienteEncontrado || agregarNuevoVehiculo) && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase tracking-wider text-slate-400">Placa *</Label>
                    <Input value={form.placa}
                      onChange={e => setForm({ ...form, placa: e.target.value.toUpperCase() })}
                      placeholder="ABC-123"
                      className="bg-slate-950 border-slate-700 text-slate-200 font-mono text-lg font-bold"
                      data-testid="turno-placa-input" /></div>
                  <div><Label className="text-xs uppercase tracking-wider text-slate-400">Tipo vehiculo</Label>
                    <Select value={form.tipo_vehiculo} onValueChange={v => setForm({ ...form, tipo_vehiculo: v })}>
                      <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="turno-tipo-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="auto" className="text-slate-300">Auto</SelectItem>
                        <SelectItem value="camioneta" className="text-slate-300">Camioneta</SelectItem>
                        <SelectItem value="suv" className="text-slate-300">SUV</SelectItem>
                        <SelectItem value="moto" className="text-slate-300">Moto</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label className="text-xs uppercase tracking-wider text-slate-400">Color (opcional)</Label>
                  <Input value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} placeholder="Ej: Rojo" className="bg-slate-950 border-slate-700 text-slate-200" /></div>
              </div>
            )}

            {/* 5. Servicios (multi-seleccion) + Notas */}
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider text-slate-400">Servicios / Paquetes *</Label>
                {form.servicios_ids.length > 0 && (
                  <span className="text-[10px] text-cyan-400 font-mono">
                    {form.servicios_ids.length} seleccionado{form.servicios_ids.length !== 1 ? 's' : ''} · {formatoMXN(
                      servicios.filter(s => form.servicios_ids.includes(s.id)).reduce((acc, s) => acc + (Number(s.precio) || 0), 0)
                    )}
                  </span>
                )}
              </div>
              <div className="mt-1 max-h-52 overflow-y-auto rounded-sm border border-slate-700 bg-slate-950 divide-y divide-slate-800" data-testid="turno-servicios-list">
                {servicios.length === 0 && <p className="p-3 text-xs text-slate-500">Sin servicios activos. Crea servicios en la seccion Servicios.</p>}
                {servicios.map(s => {
                  const marcado = form.servicios_ids.includes(s.id);
                  return (
                    <label key={s.id} className="flex items-center justify-between gap-2 px-3 py-2 cursor-pointer hover:bg-slate-900/60"
                      data-testid={`turno-servicio-opt-${s.id}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <input type="checkbox" checked={marcado}
                          onChange={e => {
                            const on = e.target.checked;
                            setForm(f => ({
                              ...f,
                              servicios_ids: on
                                ? [...f.servicios_ids, s.id]
                                : f.servicios_ids.filter(id => id !== s.id),
                            }));
                          }}
                          className="w-4 h-4 rounded border-slate-600 bg-slate-800 accent-cyan-500"
                          data-testid={`turno-servicio-check-${s.id}`} />
                        <span className={`text-sm truncate ${marcado ? 'text-cyan-300 font-medium' : 'text-slate-300'}`}>{s.nombre}</span>
                      </div>
                      <span className="text-xs font-mono text-slate-400 shrink-0">{formatoMXN(s.precio)}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-[10px] text-slate-500 mt-1">Puedes agregar varios paquetes al mismo turno. El precio se suma.</p>
            </div>
            {/* 6. Asignar operario */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Asignar operario</Label>
              <select value={form.operario_id}
                onChange={e => setForm({ ...form, operario_id: e.target.value })}
                className="w-full h-10 bg-slate-950 border border-slate-700 text-slate-200 rounded-md px-2 text-sm mt-1"
                data-testid="turno-operario-select">
                <option value="">-- Sin asignar --</option>
                {operarios.map(op => <option key={op.id} value={op.id}>{op.nombre}</option>)}
              </select>
              {operarios.length === 0 && (
                <p className="text-[10px] text-amber-400 mt-1">No hay operarios registrados. Crea uno en Staff con puesto Operario o Lavador.</p>
              )}
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Notas</Label>
              <Textarea value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })} placeholder="Observaciones..." className="bg-slate-950 border-slate-700 text-slate-200" rows={2} /></div>

            {/* 7. Cargo extra flexible (opcional) */}
            <div className="border-t border-slate-800 pt-3 space-y-2" data-testid="crear-cargo-extra-section">
              <p className="text-[11px] uppercase tracking-wider text-cyan-400 font-semibold">Cargo extra (opcional)</p>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Input value={form.cargo_extra_descripcion}
                    onChange={e => setForm({ ...form, cargo_extra_descripcion: e.target.value })}
                    placeholder="Descripción (ej. Encerado premium)"
                    className="bg-slate-950 border-slate-700 text-slate-200 h-9 text-sm" data-testid="crear-cargo-extra-desc" />
                </div>
                <div>
                  <Input type="number" min="0" step="0.01" value={form.cargo_extra_monto}
                    onChange={e => setForm({ ...form, cargo_extra_monto: e.target.value })}
                    placeholder="$0.00"
                    className="bg-slate-950 border-slate-700 text-slate-200 font-mono h-9 text-sm" data-testid="crear-cargo-extra-monto" />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.cargo_extra_comision}
                  onChange={e => setForm({ ...form, cargo_extra_comision: e.target.checked })}
                  className="w-4 h-4 accent-cyan-500" data-testid="crear-cargo-extra-comision" />
                <span className="text-xs text-slate-400">Generar comisión para el empleado</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setDialog(false); resetForm(); }} className="text-slate-400">Cancelar</Button>
            <Button onClick={crearTurno} disabled={guardando} className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold" data-testid="crear-turno-btn">
              {guardando ? 'Creando...' : 'Crear Turno'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Editar Turno */}
      <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md max-h-[90vh] overflow-y-auto" data-testid="editar-turno-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-xl text-slate-100">Editar Turno {editDialog?.numero_turno}</DialogTitle>
            {editDialog?.created_at && (
              <div className="flex items-center gap-2 mt-1" data-testid="turno-creado-en">
                <span className="text-[10px] uppercase tracking-wider text-slate-500">Creado:</span>
                <span className="text-xs text-cyan-400 font-mono">
                  {(() => {
                    try {
                      const d = new Date(editDialog.created_at);
                      const fecha = d.toLocaleDateString('es-MX',
                        { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Mexico_City' });
                      const hora = d.toLocaleTimeString('es-MX',
                        { hour: '2-digit', minute: '2-digit', timeZone: 'America/Mexico_City' });
                      return `${fecha} · ${hora}`;
                    } catch { return editDialog.created_at; }
                  })()}
                </span>
              </div>
            )}
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Placa *</Label>
                <Input value={editForm.placa} onChange={e => setEditForm({ ...editForm, placa: e.target.value.toUpperCase() })} className="bg-slate-950 border-slate-700 text-slate-200 font-mono text-lg font-bold" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Tipo Vehículo</Label>
                <Select value={editForm.tipo_vehiculo} onValueChange={v => setEditForm({ ...editForm, tipo_vehiculo: v })}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="auto" className="text-slate-300">Auto</SelectItem>
                    <SelectItem value="camioneta" className="text-slate-300">Camioneta</SelectItem>
                    <SelectItem value="suv" className="text-slate-300">SUV</SelectItem>
                    <SelectItem value="moto" className="text-slate-300">Moto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Color</Label>
              <Input value={editForm.color} onChange={e => setEditForm({ ...editForm, color: e.target.value })} className="bg-slate-950 border-slate-700 text-slate-200" /></div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider text-slate-400">Servicios / Paquetes</Label>
                {editForm.servicios_ids.length > 0 && (
                  <span className="text-[10px] text-cyan-400 font-mono">
                    {editForm.servicios_ids.length} seleccionado{editForm.servicios_ids.length !== 1 ? 's' : ''} · {formatoMXN(
                      servicios.filter(s => editForm.servicios_ids.includes(s.id)).reduce((acc, s) => acc + (Number(s.precio) || 0), 0)
                    )}
                  </span>
                )}
              </div>
              <div className="mt-1 max-h-48 overflow-y-auto rounded-sm border border-slate-700 bg-slate-950 divide-y divide-slate-800" data-testid="edit-servicios-list">
                {servicios.map(s => {
                  const marcado = editForm.servicios_ids.includes(s.id);
                  return (
                    <label key={s.id} className="flex items-center justify-between gap-2 px-3 py-2 cursor-pointer hover:bg-slate-900/60">
                      <div className="flex items-center gap-2 min-w-0">
                        <input type="checkbox" checked={marcado}
                          onChange={e => {
                            const on = e.target.checked;
                            setEditForm(f => ({
                              ...f,
                              servicios_ids: on
                                ? [...f.servicios_ids, s.id]
                                : f.servicios_ids.filter(id => id !== s.id),
                            }));
                          }}
                          className="w-4 h-4 rounded border-slate-600 bg-slate-800 accent-cyan-500" />
                        <span className={`text-sm truncate ${marcado ? 'text-cyan-300 font-medium' : 'text-slate-300'}`}>{s.nombre}</span>
                      </div>
                      <span className="text-xs font-mono text-slate-400 shrink-0">{formatoMXN(s.precio)}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Cliente</Label>
                <Input value={editForm.cliente_nombre} onChange={e => setEditForm({ ...editForm, cliente_nombre: e.target.value })} className="bg-slate-950 border-slate-700 text-slate-200" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Teléfono</Label>
                <Input value={editForm.cliente_telefono} onChange={e => setEditForm({ ...editForm, cliente_telefono: e.target.value })} className="bg-slate-950 border-slate-700 text-slate-200" /></div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Asignar operario</Label>
              <select value={editForm.operario_id}
                onChange={e => setEditForm({ ...editForm, operario_id: e.target.value })}
                className="w-full h-10 bg-slate-950 border border-slate-700 text-slate-200 rounded-md px-2 text-sm mt-1"
                data-testid="edit-operario-select">
                <option value="">-- Sin asignar --</option>
                {operarios.map(op => <option key={op.id} value={op.id}>{op.nombre}</option>)}
              </select>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Notas</Label>
              <Textarea value={editForm.notas} onChange={e => setEditForm({ ...editForm, notas: e.target.value })} className="bg-slate-950 border-slate-700 text-slate-200" rows={2} /></div>

            {/* Bloque avanzado: precio, descuento, estado, pago, fecha */}
            <div className="border-t border-slate-800 pt-4 mt-2">
              <p className="text-[11px] uppercase tracking-wider text-cyan-400 mb-3 font-semibold">Ajustes avanzados</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Precio final</Label>
                  <Input type="number" step="0.01" value={editForm.precio_final}
                    onChange={e => setEditForm({ ...editForm, precio_final: e.target.value })}
                    placeholder="Deja vacío para usar suma servicios"
                    className="bg-slate-950 border-slate-700 text-slate-200 font-mono"
                    data-testid="edit-turno-precio-final" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Descuento (MXN)</Label>
                  <Input type="number" step="0.01" value={editForm.descuento_monto}
                    onChange={e => setEditForm({ ...editForm, descuento_monto: e.target.value })}
                    placeholder="0"
                    className="bg-slate-950 border-slate-700 text-slate-200 font-mono"
                    data-testid="edit-turno-descuento" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Estado</Label>
                  <select value={editForm.estado}
                    onChange={e => setEditForm({ ...editForm, estado: e.target.value })}
                    className="w-full h-10 bg-slate-950 border border-slate-700 rounded-md px-3 text-sm text-slate-200"
                    data-testid="edit-turno-estado">
                    <option value="recibido">Recibido</option>
                    <option value="en_proceso">En proceso</option>
                    <option value="listo">Listo</option>
                    <option value="entregado">Entregado</option>
                    <option value="cancelado">Cancelado</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Método pago</Label>
                  <select value={editForm.metodo_pago}
                    onChange={e => setEditForm({ ...editForm, metodo_pago: e.target.value })}
                    className="w-full h-10 bg-slate-950 border border-slate-700 rounded-md px-3 text-sm text-slate-200"
                    data-testid="edit-turno-metodo-pago">
                    <option value="efectivo">Efectivo</option>
                    <option value="tarjeta">Tarjeta</option>
                    <option value="transferencia">Transferencia</option>
                    <option value="mixto">Mixto</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Fecha del turno</Label>
                  <Input type="date" value={editForm.fecha}
                    onChange={e => setEditForm({ ...editForm, fecha: e.target.value })}
                    className="bg-slate-950 border-slate-700 text-slate-200"
                    data-testid="edit-turno-fecha" />
                </div>
                <label className="flex items-center gap-2 mt-6 cursor-pointer">
                  <input type="checkbox" checked={editForm.pagado}
                    onChange={e => setEditForm({ ...editForm, pagado: e.target.checked })}
                    className="w-4 h-4 accent-emerald-500"
                    data-testid="edit-turno-pagado" />
                  <span className="text-sm text-slate-200">Marcado como pagado</span>
                </label>
              </div>
            </div>

            {/* Cargo extra flexible — 100% editable */}
            <div className="border-t border-slate-800 pt-4 mt-2 space-y-2" data-testid="edit-cargo-extra-section">
              <p className="text-[11px] uppercase tracking-wider text-cyan-400 font-semibold">Cargo extra</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Descripción</Label>
                  <Input value={editForm.cargo_extra_descripcion}
                    onChange={e => setEditForm({ ...editForm, cargo_extra_descripcion: e.target.value })}
                    placeholder="Ej: Encerado premium"
                    className="bg-slate-950 border-slate-700 text-slate-200" data-testid="edit-cargo-extra-desc" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Monto (MXN)</Label>
                  <Input type="number" min="0" step="0.01" value={editForm.cargo_extra_monto}
                    onChange={e => setEditForm({ ...editForm, cargo_extra_monto: e.target.value })}
                    placeholder="0"
                    className="bg-slate-950 border-slate-700 text-slate-200 font-mono" data-testid="edit-cargo-extra-monto" />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={editForm.cargo_extra_comision}
                  onChange={e => setEditForm({ ...editForm, cargo_extra_comision: e.target.checked })}
                  className="w-4 h-4 accent-cyan-500" data-testid="edit-cargo-extra-comision" />
                <span className="text-sm text-slate-200">Generar comisión para el empleado</span>
              </label>
            </div>

            {editDialog && (
              <FotosSubir tipo="recepcion_turno" entidadId={editDialog.id}
                titulo="Fotos de recepción" maxFotos={6} />
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialog(null)} className="text-slate-400">Cancelar</Button>
            <Button onClick={guardarEdicion} disabled={guardando} className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold" data-testid="guardar-edicion-btn">
              {guardando ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Cobrar (rediseño con productos de mostrador) */}
      <Dialog open={!!pagoDialog} onOpenChange={(v) => { if (!v) { setPagoDialog(null); setTurnoActivo(null); setDescuentoInfo(null); setAplicarDescuento(false); setDescuentoManual(''); setCargoExtraDesc(''); setCargoExtraMonto(''); setCargoExtraComision(false); } }}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-5xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="pago-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-2xl text-slate-100 flex items-center gap-3">
              <CreditCard className="w-6 h-6 text-green-400" /> Cobrar Turno
              {turnoActivo && <span className="text-sm font-normal text-slate-400">· {turnoActivo.placa} · {turnoActivo.numero_turno}</span>}
            </DialogTitle>
          </DialogHeader>
          {turnoActivo && (() => {
            const porcentaje = aplicarDescuento ? (parseFloat(descuentoManual) || 0) : 0;
            const precioServicios = turnoActivo.precio || 0;
            const descuentoMonto = porcentaje > 0 ? Math.round(precioServicios * porcentaje / 100 * 100) / 100 : 0;
            const totalProductos = (turnoActivo.productos_extra || []).reduce((s, p) => s + (p.subtotal || 0), 0);
            const cargoExtra = Math.max(0, parseFloat(cargoExtraMonto) || 0);
            const totalFinal = (precioServicios - descuentoMonto) + totalProductos + cargoExtra;

            return (
              <div className="grid md:grid-cols-5 gap-4 overflow-hidden flex-1">
                {/* IZQUIERDA — Grid de productos */}
                <div className="md:col-span-3 flex flex-col overflow-hidden">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs uppercase tracking-wider text-slate-400">Agregar productos del mostrador</p>
                    <span className="text-[10px] text-slate-500">Toca para agregar al ticket</span>
                  </div>
                  <div className="flex-1 overflow-y-auto pr-1">
                    {productosMostrador.length === 0 ? (
                      <div className="text-center py-10 text-slate-500 border border-dashed border-slate-800 rounded-sm">
                        <p className="text-sm">Sin productos disponibles</p>
                        <p className="text-xs mt-1">Marca productos como &quot;Visible en Mostrador&quot; en la sección Mostrador.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="productos-mostrador-grid">
                        {productosMostrador.map(p => (
                          <button key={p.id}
                            onClick={() => agregarProductoAlTurno(p)}
                            disabled={agregandoProd === p.id || p.stock <= 0}
                            className={`group relative p-3 rounded-sm border-2 transition-all text-left ${
                              p.stock <= 3
                                ? 'border-amber-800/40 bg-amber-500/5 hover:border-amber-500'
                                : 'border-slate-800 bg-slate-950/60 hover:border-cyan-500 hover:bg-cyan-500/5'
                            } disabled:opacity-40 disabled:cursor-not-allowed`}
                            data-testid={`prod-btn-${p.id}`}>
                            <div className="flex items-start justify-between mb-1">
                              <p className="text-sm font-bold text-slate-100 line-clamp-2 leading-tight">{p.nombre}</p>
                              <Badge className={`text-[9px] shrink-0 ml-1 ${p.stock <= 3 ? 'bg-amber-500/20 text-amber-400 border-amber-800' : 'bg-slate-800 text-slate-400 border-slate-700'}`}>{p.stock}</Badge>
                            </div>
                            <p className="text-lg font-mono text-cyan-400 font-bold">{formatoMXN(p.precio)}</p>
                            {p.descripcion && <p className="text-[10px] text-slate-500 line-clamp-1 mt-0.5">{p.descripcion}</p>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* DERECHA — Resumen / ticket */}
                <div className="md:col-span-2 flex flex-col bg-slate-950/40 rounded-sm border border-slate-800 p-3 overflow-hidden">
                  <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Ticket</p>
                  <div className="flex-1 overflow-y-auto space-y-1.5 mb-2" data-testid="ticket-lines">
                    {/* Servicios */}
                    {(turnoActivo.servicios && turnoActivo.servicios.length > 0
                      ? turnoActivo.servicios
                      : [{ nombre: turnoActivo.servicio_nombre, precio: turnoActivo.precio }]
                    ).map((s, i) => (
                      <div key={`s-${i}`} className="flex items-center justify-between text-xs py-1 border-b border-slate-800/60">
                        <span className="text-cyan-400 truncate">• {s.nombre}</span>
                        <span className="font-mono text-slate-300 shrink-0">{formatoMXN(s.precio)}</span>
                      </div>
                    ))}
                    {/* Productos extra */}
                    {(turnoActivo.productos_extra || []).map(p => (
                      <div key={p.id} className="flex items-center justify-between text-xs py-1 border-b border-slate-800/60 group" data-testid={`ticket-prod-${p.producto_id}`}>
                        <span className="text-slate-300 truncate">{p.cantidad}× {p.nombre}</span>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="font-mono text-slate-300">{formatoMXN(p.subtotal)}</span>
                          <button onClick={() => quitarProductoDelTurno(p.id)}
                            className="text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100"
                            data-testid={`quitar-prod-${p.id}`}>
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Totales */}
                  <div className="space-y-0.5 text-xs border-t border-slate-800 pt-2">
                    <div className="flex justify-between text-slate-500">
                      <span>Servicios</span>
                      <span className="font-mono">{formatoMXN(precioServicios)}</span>
                    </div>
                    {totalProductos > 0 && (
                      <div className="flex justify-between text-slate-500">
                        <span>Productos ({(turnoActivo.productos_extra || []).length})</span>
                        <span className="font-mono">{formatoMXN(totalProductos)}</span>
                      </div>
                    )}
                    {descuentoMonto > 0 && (
                      <div className="flex justify-between text-amber-400">
                        <span>Descuento {porcentaje}%</span>
                        <span className="font-mono">-{formatoMXN(descuentoMonto)}</span>
                      </div>
                    )}
                    {cargoExtra > 0 && (
                      <div className="flex justify-between text-cyan-300" data-testid="ticket-cargo-extra-line">
                        <span className="truncate">{cargoExtraDesc.trim() || 'Cargo extra'}</span>
                        <span className="font-mono shrink-0">{formatoMXN(cargoExtra)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-bold pt-1 border-t border-slate-800 mt-1">
                      <span className="text-slate-300">Total</span>
                      <span className="font-mono text-green-400">{formatoMXN(totalFinal)}</span>
                    </div>
                  </div>

                  {/* Descuento manual */}
                  {(!descuentoInfo || (!descuentoInfo.es_frecuente && !descuentoInfo.es_premio)) && (
                    <div className="mt-2 flex items-center gap-2 p-2 rounded-sm border border-slate-800">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" checked={aplicarDescuento} onChange={e => {
                          setAplicarDescuento(e.target.checked);
                          if (e.target.checked && !descuentoManual) setDescuentoManual('10');
                        }}
                          className="w-4 h-4 accent-cyan-500" data-testid="descuento-manual-checkbox" />
                        <span className="text-[10px] text-slate-400">Descuento manual</span>
                      </label>
                      {aplicarDescuento && (
                        <Input type="number" value={descuentoManual} onChange={e => setDescuentoManual(e.target.value)}
                          min="0" max="100" step="1" className="bg-slate-950 border-slate-700 text-slate-200 font-mono w-14 h-6 text-xs ml-auto" data-testid="descuento-manual-input" />
                      )}
                    </div>
                  )}

                  {/* Cargo extra flexible */}
                  <div className="mt-2 p-2 rounded-sm border border-slate-800 space-y-1.5" data-testid="cargo-extra-section">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500">Cargo extra</p>
                    <Input value={cargoExtraDesc} onChange={e => setCargoExtraDesc(e.target.value)}
                      placeholder="Descripción (ej. Encerado premium)"
                      className="bg-slate-950 border-slate-700 text-slate-200 h-8 text-xs" data-testid="cargo-extra-desc" />
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 font-mono">$</span>
                      <Input type="number" min="0" step="0.01" value={cargoExtraMonto}
                        onChange={e => setCargoExtraMonto(e.target.value)}
                        placeholder="0.00"
                        className="bg-slate-950 border-slate-700 text-slate-200 font-mono h-8 text-xs" data-testid="cargo-extra-monto" />
                    </div>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={cargoExtraComision}
                        onChange={e => setCargoExtraComision(e.target.checked)}
                        className="w-4 h-4 accent-cyan-500" data-testid="cargo-extra-comision-checkbox" />
                      <span className="text-[10px] text-slate-400">Generar comisión para el empleado</span>
                    </label>
                  </div>

                  {/* Operario asignado (reasignable) */}
                  <div className="mt-2">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                      <UserCircle className="w-3 h-3" /> Operario (comisión)
                    </Label>
                    <select
                      value={turnoActivo.operario_id || ''}
                      onChange={(e) => {
                        const opId = e.target.value;
                        const op = operarios.find(o => o.id === opId);
                        setTurnoActivo({ ...turnoActivo, operario_id: opId, operario_nombre: op?.nombre || '' });
                      }}
                      className="w-full h-9 bg-slate-950 border border-slate-700 text-slate-200 rounded-md px-2 text-xs mt-0.5"
                      data-testid="pago-operario-select">
                      <option value="">-- Sin asignar --</option>
                      {operarios.map(op => <option key={op.id} value={op.id}>{op.nombre}</option>)}
                    </select>
                  </div>

                  {/* Método de pago */}
                  <div className="mt-2">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Método</Label>
                    <Select value={metodoPago} onValueChange={setMetodoPago}>
                      <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200 h-9 mt-0.5" data-testid="metodo-pago-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="efectivo" className="text-slate-300">Efectivo</SelectItem>
                        <SelectItem value="tarjeta" className="text-slate-300">Tarjeta</SelectItem>
                        <SelectItem value="transferencia" className="text-slate-300">Transferencia</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            );
          })()}
          <DialogFooter className="pt-2">
            <Button variant="ghost" onClick={() => { setPagoDialog(null); setTurnoActivo(null); setDescuentoInfo(null); setAplicarDescuento(false); setDescuentoManual(''); setCargoExtraDesc(''); setCargoExtraMonto(''); setCargoExtraComision(false); }} className="text-slate-400">Cancelar</Button>
            <Button onClick={() => pagarTurno(pagoDialog?.id)} className="bg-green-500 hover:bg-green-600 text-black font-bold h-11 px-6" data-testid="confirmar-pago-btn">
              <CreditCard className="w-4 h-4 mr-2" /> Confirmar Cobro
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
